/**
 * The smallest IndexedDB this app can be built on, and the only place in it
 * that talks to the database directly.
 *
 * There is exactly one thing here that localStorage could not do, and it is the
 * whole reason for the file: IndexedDB stores a Blob. A queued send is a video
 * the phone has not finished uploading, and surviving the app being closed
 * means keeping the bytes, not a description of them. Everything else this app
 * persists — the theme, the error queue, the smiley stats — is small and
 * synchronous and stays in localStorage where it is.
 *
 * No library. `idb` is 2 КБ of promise-wrapping and would be a reasonable
 * dependency; this is 60 lines of the same, and it is the only consumer.
 */

const DB_NAME = 'chat';
const DB_VERSION = 1;

/** The queued sends themselves — small records, rewritten on every chunk. */
export const OUTBOX_STORE = 'outbox';
/**
 * Their files, keyed separately and never rewritten.
 *
 * Two stores rather than one because of how often the first is written: a send
 * in flight updates its offsets every couple of seconds, and if the blobs lived
 * on the same record then every one of those updates would re-serialise fifty
 * megabytes of video to write down a number.
 */
export const BLOB_STORE = 'outbox-blobs';

let opening: Promise<IDBDatabase> | null = null;

function open(): Promise<IDBDatabase> {
  opening ??= new Promise<IDBDatabase>((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(OUTBOX_STORE)) db.createObjectStore(OUTBOX_STORE);
      if (!db.objectStoreNames.contains(BLOB_STORE)) db.createObjectStore(BLOB_STORE);
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error ?? new Error('indexedDB open failed'));
    // Another tab holding an old version open. Nothing here can proceed, and
    // rejecting is what turns that into "the outbox is unavailable" rather than
    // a promise that never settles and a send that never starts.
    request.onblocked = () => reject(new Error('indexedDB blocked'));
  });
  return opening;
}

function run<T>(
  store: string,
  mode: IDBTransactionMode,
  work: (store: IDBObjectStore) => IDBRequest<T>,
): Promise<T> {
  return open().then(
    (db) =>
      new Promise<T>((resolve, reject) => {
        const transaction = db.transaction(store, mode);
        const request = work(transaction.objectStore(store));
        request.onsuccess = () => resolve(request.result);
        // The request's own error and the transaction's are both listened for:
        // a quota failure surfaces on the transaction alone, and that is the
        // one this app actually hits — a phone with a full disk and a video.
        request.onerror = () => reject(request.error ?? new Error('indexedDB request failed'));
        transaction.onabort = () =>
          reject(transaction.error ?? new Error('indexedDB transaction aborted'));
      }),
  );
}

export function idbGet<T>(store: string, key: string): Promise<T | undefined> {
  return run<T | undefined>(store, 'readonly', (objects) => objects.get(key) as IDBRequest<T | undefined>);
}

export function idbGetAll<T>(store: string): Promise<T[]> {
  return run<T[]>(store, 'readonly', (objects) => objects.getAll() as IDBRequest<T[]>);
}

export function idbPut(store: string, key: string, value: unknown): Promise<IDBValidKey> {
  return run<IDBValidKey>(store, 'readwrite', (objects) => objects.put(value, key));
}

export function idbDelete(store: string, key: string): Promise<undefined> {
  return run<undefined>(store, 'readwrite', (objects) => objects.delete(key) as IDBRequest<undefined>);
}

/** Whether this browser has one at all — a private window may not. */
export function hasIdb(): boolean {
  return typeof indexedDB !== 'undefined';
}
